import downloadFile from 'downloadjs'
import React, { createContext, ReactNode, useContext, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { useBeforeUnload } from 'react-use'
import { WEB_API_KEY } from 'shared/api'
import MessageContext from 'shared/components/context/message'
import UserContext from 'shared/components/context/user'
import { CreationToolsContext } from '../../manager'
import { getBasePathFromCreationTool } from '../../routes'
import CreationTool from '../../types/creation-tool'
import CreationToolError from '../../types/error'
import CreationToolGAEvent from '../../types/ga-event'
import CreationToolOutputType from '../../types/output-type'
import CreationToolStatus from '../../types/status'
import getDefaultSourceTitle from '../../utils/get-default-source-title'
import getGifFromFrames from '../../utils/get-gif-from-frames'
import { uploadGif } from '../../utils/upload'
import { CreationToolUploadContextProps, CreationToolUploadSource } from './types'

export const CreationToolUploadContext = createContext({} as CreationToolUploadContextProps)

export const cleanUpTags = (tags: string[]) => tags.map(tag => tag.replace(/#/g, '').trim())

type Props = {
    children: ReactNode
    submitUrl: string
    apiKey?: string
}

const CreationToolUploadContextManager = ({ children, submitUrl, apiKey }: Props) => {
    const { sources, trackEvent, tool, stepFrames, outputType } = useContext(CreationToolsContext)
    const { user } = useContext(UserContext)
    const { sendMessage } = useContext(MessageContext)
    const history = useHistory()
    const source = sources.length ? sources[0] : null
    const [sourceUrl, setSourceUrl] = useState<string>(source?.sourceUrl || '')
    const [tags, setTags] = useState<string[]>(source?.tags || [])
    const [lastEnteredTags, setLastEnteredTags] = useState<string[]>([])
    const [rating, setRating] = useState<string>(source?.rating || '')
    const [channelId, setChannelId] = useState<string>('')
    const [username, setUsername] = useState<string>(source?.username || user.username || '')
    const [description, setDescription] = useState<string>('')
    const [ctaText, setCtaText] = useState<string>('')
    const [isPublic, setIsPublic] = useState<boolean>(true)
    const [status, setStatus] = useState<CreationToolStatus>(CreationToolStatus.Idle)
    const pausedRef = useRef<boolean>(false)
    const failedUploadIdsRef = useRef<string[]>([])

    const [uploadSources, setUploadSources] = useState<CreationToolUploadSource[]>(
        sources.map(source => ({
            id: source.id,
            source,
            tags: source.tags || [],
            status: CreationToolStatus.Idle,
            title: getDefaultSourceTitle(source),
            outputType:
                outputType === CreationToolOutputType.Gif && source.isTransparent
                    ? CreationToolOutputType.Sticker // if uploading as a gif, and the gif is transparent, show sticker as output type
                    : outputType,
        }))
    )

    // page reload warning
    useBeforeUnload(
        status !== CreationToolStatus.Complete && stepFrames.length > 0,
        `You'll lose all your beautiful work!`
    )

    const addTags = (newTags: string[], isSuggested: boolean = false, sourceId?: string) => {
        if (newTags.length < 1) return
        const cleaned = cleanUpTags(newTags)

        // updating specific source
        if (sourceId) {
            const source = uploadSources.find(src => src.id === sourceId)
            if (source) {
                const allTags = [...source.tags, ...cleaned]
                updateUploadSource({ ...source, tags: allTags })
            }
            return
        }

        const allTags = [...tags, ...cleaned]
        if (!isSuggested) {
            setLastEnteredTags(cleaned)
        }
        setTags(allTags.filter((tag, i) => allTags.indexOf(tag) === i))
    }

    const removeTags = (removedTags: string[], sourceId?: string) => {
        if (removedTags.length < 1) return
        const cleaned = cleanUpTags(removedTags)

        // updating specific source
        if (sourceId) {
            const source = uploadSources.find(src => src.id === sourceId)
            if (source) {
                const allTags = source.tags.filter(tag => cleaned.indexOf(tag) < 0)
                updateUploadSource({ ...source, tags: allTags })
            }
            return
        }

        setTags(tags => tags.filter(tag => cleaned.indexOf(tag) < 0))
    }

    const updateUploadSource = (source: CreationToolUploadSource) => {
        setUploadSources(uploadSources =>
            uploadSources.map(src => {
                return src.id === source.id ? source : src
            })
        )
    }

    const removeUploadSource = (sourceId: string) => {
        const updatedSources = uploadSources.filter(src => src.id !== sourceId)
        if (updatedSources.length) {
            setUploadSources(updatedSources)
        } else {
            // redirect to welcome screen since there are no more sources left
            history.replace(getBasePathFromCreationTool(tool))
        }
    }

    const pause = () => {
        pausedRef.current = true
        setStatus(CreationToolStatus.Idle)
    }

    const upload = async (uploads: CreationToolUploadSource[]) => {
        let generatedFile: File | null = null
        let successfulUploadCount = 0

        // unset paused state
        pausedRef.current = false

        // if there are frames and only one source, generate a gif from the frames
        if (uploads.length === 1 && stepFrames.length > 0) {
            setStatus(CreationToolStatus.Generating)
            generatedFile = await getGifFromFrames(stepFrames)
        }

        // set the global status of uploading
        setStatus(CreationToolStatus.Uploading)

        const filteredUploads = uploads.filter(
            src => !src.error && !src.uploadedId && src.status === CreationToolStatus.Idle
        )

        for (let i = 0; i < filteredUploads.length; i++) {
            const upload = filteredUploads[i]
            const { source: src } = upload

            // stop if paused
            if (pausedRef.current) {
                break
            }

            // update the status on the upload source
            updateUploadSource({ ...upload, status: CreationToolStatus.Uploading })
            trackEvent(CreationToolGAEvent.outputTypeCategory(upload.outputType), 'Creation Tool Upload', 'Start')

            try {
                const creationLocationPrefix = CreationTool.creationLocationPrefix(tool, uploads.length > 1)
                const creationLocationSuffix = CreationToolOutputType.creationLocationSuffix(outputType)
                const uploadedId = await uploadGif(submitUrl, generatedFile || src.source, {
                    apiKey: apiKey || WEB_API_KEY,
                    outputType: upload.outputType || outputType,
                    title: upload.title,
                    description: upload.description || description,
                    userToken: user.userToken,
                    tags: [...tags, ...upload.tags, CreationTool.uploadTag(tool)],
                    creationLocation: `${creationLocationPrefix}_${creationLocationSuffix}`,
                    sourceUrl,
                    ctaText,
                    rating,
                    channelId,
                    username,
                    isPublic,
                })

                trackEvent(CreationToolGAEvent.outputTypeCategory(outputType), 'Creation Tool Upload', 'Success')
                successfulUploadCount++

                if (uploadSources.length === 1) {
                    setStatus(CreationToolStatus.Complete)
                    // redirect to the upload if not bulk
                    setTimeout(() => {
                        location.href = `${CreationToolOutputType.uploadedBasePath(upload.outputType)}/${uploadedId}`
                    }, 300)
                } else {
                    updateUploadSource({ ...upload, uploadedId, status: CreationToolStatus.Idle })
                }
            } catch (err) {
                let error = CreationToolError.fromServerResponse(err)

                // check if error is upload failed and is second time
                if (error === CreationToolError.UploadFailed && failedUploadIdsRef.current.indexOf(upload.id) > -1) {
                    error = CreationToolError.UploadFailedAgain
                }

                // display error banner
                const errorText = CreationToolError.description(error)
                sendMessage(errorText, 'error')

                // if bulk, add error to individual item.
                // otherwise reset everything.
                if (uploadSources.length > 1) {
                    updateUploadSource({ ...upload, status: CreationToolStatus.Idle, error })
                } else {
                    updateUploadSource({ ...upload, status: CreationToolStatus.Idle })
                    setStatus(CreationToolStatus.Idle)
                }

                failedUploadIdsRef.current.push(upload.id)
            }
        }

        if (uploadSources.length > 1) {
            setStatus(CreationToolStatus.Idle)
        }

        if (successfulUploadCount > 0) {
            sendMessage('Upload Complete!', 'success')
        }
    }

    const download = async () => {
        if (!stepFrames.length) return

        trackEvent(CreationToolGAEvent.outputTypeCategory(outputType), 'Creation Tool Download', 'Start')
        try {
            setStatus(CreationToolStatus.Generating)
            const gif = await getGifFromFrames(stepFrames)
            setStatus(CreationToolStatus.Downloading)
            downloadFile(gif, 'giphy.gif')
            setStatus(CreationToolStatus.Idle)
            trackEvent(CreationToolGAEvent.outputTypeCategory(outputType), 'Creation Tool Download', 'Success')
        } catch (error) {
            sendMessage(`Error downloading your file :(`, 'error')
            setStatus(CreationToolStatus.Idle)
        }
    }

    return (
        <CreationToolUploadContext.Provider
            value={{
                allowDownload: stepFrames.length > 0 && sources.length === 1,
                uploadSources,
                status,
                tags,
                lastEnteredTags,
                sourceUrl,
                rating,
                description,
                channelId,
                username,
                isPublic,
                ctaText,
                addTags,
                removeTags,
                updateUploadSource,
                removeUploadSource,
                setSourceUrl,
                setRating,
                setDescription,
                setChannelId,
                setUsername,
                setStatus,
                setIsPublic,
                setCtaText,
                upload,
                download,
                pause,
            }}
        >
            {children}
        </CreationToolUploadContext.Provider>
    )
}

export default CreationToolUploadContextManager
